import compose from '@hypnosphi/recompose/compose';
import XmlBeautify from 'xml-beautify';
import jsonrepair from 'jsonrepair';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import TextEditor from '../texteditor'
import Tooltip from 'apollo-react/components/Tooltip';
import { withStyles } from '@mui/styles';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { usePostmongerContext } from '../../contexts/PostmongerContext';

export const XML = {
  format(xml) {
    return new XmlBeautify().beautify(xml, {
      indent: '  ',
      useSelfClosingElement: true,
    });
  },
  validate(xml, batchSize = 1, translation = id => id) {
    const parsed = new DOMParser().parseFromString(xml, 'text/xml');
    const errors = parsed.documentElement.getElementsByTagName('parsererror');
    if (errors.length > 0) {
      const divs = errors[0].getElementsByTagName('div');
      if (divs.length > 0) {
        return { err: { msg: divs[0].textContent } };
      }
      return { err: { msg: errors[0].textContent } };
    } else if (batchSize > 1) {
      const contacts = parsed.documentElement.querySelectorAll('#contact');
      if (contacts.length === 0) {
        return { err: { msg: translation('webhook.missingContactNode') } };
      } else if (contacts.length > 1) {
        return { err: { msg: translation('webhook.tooManyContactNode') } };
      }      
    }

    return true;
  },
};

const styles = theme => ({
  section: {
    marginBottom: 32,
  },
  warning: {
    color: theme.palette.warning.main,
  },
  hidden: {
    display: 'none',
  },
});

// FIELDS USED IN inArguments
const fieldNames = {
  profile: 'webhook.Profile',
  channel: 'webhook.Channel',
  payload: 'webhook.Payload',
  profileName: 'webhook.Name',
  endpointUrl: 'webhook.EndpointURL',
};

const initialState = {
  mouseX: null,
  mouseY: null,
};

const Webhook = (props, ref) => {
  const { t } = useTranslation();

  const {
    inArguments,
    setDirty,
    seedData, 
    disabled,
  } = usePostmongerContext();

  const  { classes } = props;

  const { profiles, channels, fields } = (seedData && seedData.Webhook) ? seedData.Webhook : { };

  const availableProfiles = profiles || [];
  const availableChannels = channels || [];
  const availableFields = fields || [];

  const getProfile = (id) =>
    availableProfiles.find((p) => p.Id === id) || {};

  const profilesMenuItems = availableProfiles
    .sort((a, b) => a.Name.localeCompare(b.Name))
    .map(profile => (<MenuItem key={profile.Id} value={profile.Id}>{profile.Name}</MenuItem>));

  const channelsMenuItems = [
    <MenuItem key={''} value={''}>{t('generic.none')}</MenuItem>,
    ...availableChannels
        .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))
        .map(channel => (
          <MenuItem key={channel.ChannelCode} value={channel.ChannelCode}>
            {channel.DisplayName}
          </MenuItem>
        )),
  ];

  const defaultProfile = () => inArguments.get(fieldNames.profile);
  const defaultChannel = () => inArguments.get(fieldNames.channel);
  const defaultPayload = () => inArguments.get(fieldNames.payload);

  const [state, setState] = useState(initialState);
  const [webhookProfile, setWebhookProfile] = useState(defaultProfile());
  const [webhookChannel, setWebhookChannel] = useState(defaultChannel());
  const [webhookPayload, setWebhookPayload] = useState(defaultPayload());

  // Set profile selection and initialize payload with the one from the profile

  const onChangeWebhookProfile = event => {
    if (event.target.value === webhookProfile) {
      return; // no change
    }

    const profile = getProfile(event.target.value);

    setWebhookProfile(profile.Id);
    inArguments.set(fieldNames.profile, profile.Id);
    inArguments.set(fieldNames.profileName, profile.Name);
    inArguments.set(fieldNames.endpointUrl, profile.EndpointURL);

    updatePayload(profile.Payload); 
    setState({ 
      ...state, 
      payloadError: undefined, 
      missingProfile: profile.IsActive ? undefined : t('webhook.ProfileError'),
      inactiveChannel: undefined,
    });
    setDirty();
  };

  // Set channel selection

  const onChangeWebhookChannel = event => {
    setWebhookChannel(event.target.value);
    inArguments.set(fieldNames.channel, event.target.value);
    setState({...state, inactiveChannel: undefined});
    setDirty();
  };

  // Set payload content from the payload of the profile

  const onChangePayload = event => {
    const payload = event.target.value;
    if (payload !== webhookPayload) {
      setWebhookPayload(payload);
      inArguments.set(fieldNames.payload, payload);
      setDirty();
    }
  };

  const updatePayload = newPayload => onChangePayload({ target: { value: newPayload } }); // emulate event

  // initialize default values

  let profileActive = true;

  if (webhookProfile !== defaultProfile()) {
    setWebhookProfile(defaultProfile());
  } else if (webhookProfile) {
    const profileName = inArguments.get(fieldNames.profileName) || webhookProfile; // fallback to showing id    
    profileActive = !!getProfile(webhookProfile).Id;

    if (!profileActive) { 
      // not found so must be inactive => Add to list but hidden. That way is show but not when the list is opened
      profilesMenuItems.push(
        <MenuItem key={webhookProfile} value={webhookProfile} className={classes.hidden}>
          {profileName}
        </MenuItem>
      );
    } 
  }

  if (webhookChannel !== defaultChannel()) {
    setWebhookChannel(defaultChannel());
  }
  if (webhookPayload !== defaultPayload()) {
    setWebhookPayload(defaultPayload());
  }

  const resetPayload = () => {
    const profile = getProfile(webhookProfile);
    const payload = profile.Payload;

    updatePayload(payload);
    setState({...state, payloadError: undefined});
  }

  const validatePayload = (payload, profile, noFormatting) => {
    try {
      if (!payload) {
        setState({...state, payloadError: undefined});
        return { formatted: payload, valid: true };
      }

      noFormatting = !!noFormatting;

      switch (profile.ContentType) {
        case 'application/json': {
          const data = noFormatting ? payload : jsonrepair(payload);
          const jObj = JSON.parse(data);
          const formatted = noFormatting ? payload : JSON.stringify(jObj, undefined, '  ');
          setState({...state, payloadError: undefined});
          return { formatted, valid: true };
        }
        case 'text/xml': {
          const status = XML.validate(payload, profile.BatchSize, t);
          if (status !== true) {
            setState({ ...state, payloadError: status.err.msg });
            return { formatted: payload, valid: false };
          }
          setState({...state, payloadError: undefined});
          return { formatted: noFormatting ? payload : XML.format(payload), valid: true };
        }
        default:
          setState({ ...state, payloadError: t('webhook.invalidContentType') });
          return { formatted: payload, valid: false };
      }
    } catch (error) {
      setState({ ...state, payloadError: error.toString() });
      return { formatted: payload, valid: false };
    }
  };

  const formatPayload = () => {
    try {
      const { formatted, valid } = validatePayload(webhookPayload, getProfile(webhookProfile));

      if (valid && formatted !== webhookPayload) {
        updatePayload(formatted);
      }
    } catch (error) {
      setState({ ...state, payloadError: error.toString() });
    }
  };

  const toolbar = [
    { 
      label: t('webhook.toolbarFormat'), 
      tooltip: t('webhook.toolbarFormatTooltip'), 
      action: formatPayload, 
      disabled: !webhookPayload 
    },
    { 
      label: t('webhook.toolbarReset'), 
      tooltip: t('webhook.toolbarResetTooltip'),
      action: resetPayload, 
    },
  ];

  const isValid = () =>  {
    // Is a channel selected that is no longer valid?
    if (!disabled && inArguments.get(fieldNames.channel) && inArguments.get(fieldNames.channel) !== '')
    {
      if (!availableChannels.find(channel => channel.ChannelCode == inArguments.get(fieldNames.channel)))
      {
        setState({...state, inactiveChannel: t('generic.inactiveChannelSelected')});
        return false;
      }
    }
    
    // Ensure a profile is selected
    const profile = getProfile(defaultProfile());
    if (!profile.IsActive) {
      setState({...state, missingProfile: t('webhook.profileError')});
      return false;
    }
    // Check that the payload is valid
    const payload = defaultPayload();
    const { valid } = validatePayload(payload, profile, true);
    if (!valid) {
      return false;
    }

    // All is Good
    return true;
  };

  useImperativeHandle(ref, () => ({ isValid }));

  const getAvailableFields = _ => {
    switch (getProfile(webhookProfile).AuthenticationType)
    {
      case 'BASIC':
        return ['NOW', 'USER_NAME', 'USER_PASSWORD', ...availableFields.sort()];

      case 'API_TOKEN':
        return ['API_TOKEN', 'NOW', ...availableFields.sort()];

      case 'DYNAMIC_TOKEN': 
      case 'ACCESS_TOKEN':
        return ['ACCESS_TOKEN', 'NOW', ...availableFields.sort()];

      default: 
        return ['NOW', ...availableFields.sort()];
    }
  };

  return (
    <div className={classes.section}>
      {disabled && <TextField
            fullWidth 
            label={t('webhook.consentCaption')} 
            value={webhookChannel || t('generic.none')}
            disabled={disabled}
          />}
      {!disabled && <Tooltip title={t('webhook.consentTooltip')}>
        <Select
          data-testid="channels"
          name={fieldNames.channel}
          label={t('webhook.consentCaption')}
          value={webhookChannel}
          fullWidth
          canDeselect={true}
          disabled={disabled}
          onChange={onChangeWebhookChannel}
          helperText={ state.inactiveChannel }
          error={!!state.inactiveChannel}
        >
          {channelsMenuItems}
        </Select>
      </Tooltip>}

      <Tooltip title={t('webhook.profileTooltip')}>
        <Select
          data-testid="profiles"
          name={fieldNames.profile}
          label={t('webhook.profileCaption')}
          value={webhookProfile}
          fullWidth
          required
          canDeselect={false}
          disabled={disabled}
          onChange={onChangeWebhookProfile}
          helperText={ 
            state.missingProfile ||
            (!profileActive && (<span className={classes.warning}>{t('webhook.profileWarning')}</span>))
          }
          error={!!state.missingProfile}
        >
          {profilesMenuItems}
        </Select>
      </Tooltip>

      {webhookProfile && 
        <TextEditor
          data-testid="payloadEditor"
          id='payloadEditor'
          name={fieldNames.payload}
          toolbar={toolbar}
          label={t('webhook.payloadCaption')}
          value={webhookPayload}
          fields={getAvailableFields()}
          tooltip={t('webhook.payloadTooltip')}
          error={state.payloadError}
          disabled={disabled || !profileActive}
          onChange={onChangePayload}
        />
      }
    </div>
  );
};

export default compose(withStyles(styles))(forwardRef(Webhook));
